import {
  ActionIcon,
  AppShell,
  Box,
  Container,
  Flex,
  Group,
  Image,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCompass,
  IconMail,
  IconPackage,
  IconTag,
  IconUser,
} from "@tabler/icons-react";
import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import fullLogo from "../assets/full_logo2.svg";
import { userInfoAtom } from "../atoms";
import TransferModal from "./Modals/TransferModal/TransferModal";
import { UserMenu } from "./UserMenu/UserMenu";

export default function BrandNavBar() {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const location = useLocation();
  const [transferModalOpened, setTransferModalOpened] = useState(false);

  // Jotai state
  const userInfo = useAtomValue(userInfoAtom);

  const handleLogout = () => {
    Cookies.remove("jwt");
    window.location.href = "/";
  };

  const profileUrlPath = userInfo?.brandId ?? userInfo?.email;
  const navItems = [
    { label: "Discover", icon: IconCompass, path: "/discover" },
    ...(userInfo?.userType === "BRAND_USER"
      ? [{ label: "Products", icon: IconPackage, path: "/products" }]
      : []),
    { label: "Collabs", icon: IconTag, path: "/collabs" },
    ...(userInfo
      ? [
          {
            label: "Profile",
            icon: IconUser,
            path: `/profile/${profileUrlPath}`,
          },
        ]
      : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  // Desktop navigation
  const DesktopNav = (
    <AppShell mb={"74px"}>
      <AppShell.Header>
        <Container size="xl" py="md">
          <Group justify="space-between" wrap="nowrap">
            <Group style={{ flexShrink: 0 }}>
              <Link to="/">
                <Image
                  src={fullLogo}
                  alt="Collabish Logo"
                  h={40}
                  fit="contain"
                  style={{ display: "block", maxWidth: "150px" }}
                />
              </Link>
            </Group>

            <Group>
              {navItems.map((item) => (
                <UnstyledButton
                  key={item.label}
                  component={Link}
                  to={item.path}
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                    backgroundColor: isActive(item.path)
                      ? theme.colors.blue[0]
                      : "transparent",
                    color: isActive(item.path)
                      ? theme.colors.blue[7]
                      : theme.colors.gray[7],
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                >
                  <Group gap="xs">
                    <item.icon size={18} />
                    <Text>{item.label}</Text>
                  </Group>
                </UnstyledButton>
              ))}
            </Group>

            {userInfo ? (
              <Group gap="md">
                <UnstyledButton
                  component={Link}
                  to="/contact"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                    backgroundColor: isActive("/contact")
                      ? theme.colors.blue[0]
                      : "transparent",
                    color: isActive("/contact")
                      ? theme.colors.blue[7]
                      : theme.colors.gray[7],
                    fontWeight: isActive("/contact") ? 600 : 400,
                  }}
                >
                  <Group gap="xs">
                    <IconMail size={18} />
                    <Text>Contact Us</Text>
                  </Group>
                </UnstyledButton>
                <UserMenu
                  handleLogout={handleLogout}
                  onTransferClick={() => setTransferModalOpened(true)}
                />
              </Group>
            ) : (
              <Group>
                <UnstyledButton
                  component={Link}
                  to="/contact"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                  }}
                >
                  Contact
                </UnstyledButton>
                <UnstyledButton
                  component={Link}
                  to="/login"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                  }}
                >
                  Login
                </UnstyledButton>
                <UnstyledButton
                  component={Link}
                  to="/signup"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.blue[6],
                    color: "white",
                  }}
                >
                  Sign Up
                </UnstyledButton>
              </Group>
            )}
          </Group>
        </Container>
      </AppShell.Header>
    </AppShell>
  );

  // Mobile bottom navigation
  const MobileNav = (
    <Box
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "white",
        borderTop: `1px solid ${theme.colors.gray[2]}`,
        zIndex: 1000,
      }}
    >
      <Flex justify="space-around" p="xs">
        {navItems.map((item) => (
          <UnstyledButton
            key={item.label}
            component={Link}
            to={item.path}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              color: isActive(item.path)
                ? theme.colors.blue[7]
                : theme.colors.gray[6],
            }}
          >
            <ActionIcon
              variant={isActive(item.path) ? "light" : "subtle"}
              color={isActive(item.path) ? "blue" : "gray"}
              size="lg"
            >
              <item.icon size={22} />
            </ActionIcon>
            <Text size="xs" fw={isActive(item.path) ? 600 : 400}>
              {item.label}
            </Text>
          </UnstyledButton>
        ))}
      </Flex>
    </Box>
  );

  // Mobile header (simplified)
  const MobileHeader = (
    <AppShell>
      <AppShell.Header>
        <Container py="md">
          <Group justify="space-between" wrap="nowrap">
            <Link to="/" style={{ flexShrink: 0 }}>
              <Image
                src={fullLogo}
                alt="Collabish Logo"
                h={40}
                fit="contain"
                style={{ display: "block", maxWidth: "150px" }}
              />
            </Link>

            {userInfo ? (
              <UserMenu
                handleLogout={handleLogout}
                onTransferClick={() => setTransferModalOpened(true)}
              />
            ) : (
              <Group>
                <UnstyledButton
                  component={Link}
                  to="/login"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                  }}
                >
                  Login
                </UnstyledButton>
                <UnstyledButton
                  component={Link}
                  to="/signup"
                  style={{
                    padding: "8px 16px",
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.blue[6],
                    color: "white",
                  }}
                >
                  Sign Up
                </UnstyledButton>
              </Group>
            )}
          </Group>
        </Container>
      </AppShell.Header>
    </AppShell>
  );

  return (
    <>
      <TransferModal
        opened={transferModalOpened}
        onClose={() => setTransferModalOpened(false)}
      />
      {isMobile ? (
        <>
          {MobileHeader}
          {userInfo && MobileNav}
          {userInfo && <Box pb={60} />}
        </>
      ) : (
        DesktopNav
      )}
    </>
  );
}
